'use client';
import React, { createContext, startTransition, useContext, useEffect, useMemo } from 'react';
import { useCustomRouter } from '@benzinga/hooks';
import { raptiveAdManager } from '@benzinga/ads-utils';
import { SessionContext } from '@benzinga/session-context';
import { AuthenticationManager, AuthenticationManagerEvent } from '@benzinga/session';
import { PermissionsManager, PermissionsManagerEvent } from '@benzinga/permission-manager';
import { appEnvironment, appName } from '@benzinga/utils';
import {
  isRobinhoodPartnerReferral,
  isRobinhoodPartnerReferralFromParams,
  isSofiPartnerReferral,
  isSofiPartnerReferralFromParams,
} from '../isPartnerReferral';

interface DisabledAds {
  raptive: boolean;
  connatix: boolean;
}

interface BenzingaEdgeContextInterface {
  adLightEnabled: boolean;
  disabled: DisabledAds;
  hasEdgeSubscription: boolean;
  isEdgeSubscriptionLoading: boolean;
  isLoggedIn: boolean;
  /**
   * Reflects ad-light readiness only. Use `isEdgeSubscriptionLoading` to gate
   * paywall/feature UI on the resolution of the Edge subscription check.
   */
  isLoading: boolean;
}

const BenzingaEdgeContext = createContext<BenzingaEdgeContextInterface | null>(null);

interface BenzingaEdgeProviderProps {
  children: React.ReactNode;
  initialQueryParams?: Record<string, string>;
  initialTargeting?: Record<string, string | string[]>;
  disableRaptiveFooter?: boolean;
  disableRaptiveReadyOnPageLoad?: boolean;
}

// TODO: Change to AdLightProvider
const partnerReferralAdLightFromParams = (params?: Record<string, string>) =>
  !!(isSofiPartnerReferralFromParams(params) || isRobinhoodPartnerReferralFromParams(params));

const EDGE_PERMISSION_ACTION = 'com/read';
const EDGE_PERMISSION_RESOURCE = 'unlimited-calendars';

export const BenzingaEdgeProvider: React.FC<BenzingaEdgeProviderProps> = ({
  children,
  disableRaptiveFooter,
  disableRaptiveReadyOnPageLoad,
  initialQueryParams,
  initialTargeting,
}) => {
  const session = React.useContext(SessionContext);
  const authManager = session.getManager(AuthenticationManager);
  const permissionsManager = session.getManager(PermissionsManager);

  const router = useCustomRouter();

  const initialAdLight = partnerReferralAdLightFromParams(initialQueryParams);
  const [hasAdLight, setHasAdLight] = React.useState(initialAdLight);
  const [isConnatixDisabled, setIsConnatixDisabled] = React.useState(initialAdLight);
  const [isRaptiveDisabled, setIsRaptiveDisabled] = React.useState(initialAdLight);
  const [isAdLightLoading, setIsAdLightLoading] = React.useState(!initialAdLight);

  const [hasEdgeSubscription, setHasEdgeSubscription] = React.useState(
    () => !!permissionsManager.hasAccess(EDGE_PERMISSION_ACTION, EDGE_PERMISSION_RESOURCE)?.ok,
  );
  const [isLoggedIn, setIsLoggedIn] = React.useState(() => authManager.isLoggedIn());
  const [isEdgeLoading, setIsEdgeLoading] = React.useState(true);

  useEffect(() => {
    if (initialAdLight) return;
    const isReferredBySofi = isSofiPartnerReferral(router.query as Record<string, string>);
    const isReferredByRobinhood = isRobinhoodPartnerReferral(router.query as Record<string, string>);
    const shouldReferrerHaveAdLight = isReferredBySofi || isReferredByRobinhood;
    if (shouldReferrerHaveAdLight) {
      setHasAdLight(true);
      setIsConnatixDisabled(true);
      setIsRaptiveDisabled(true);
      setIsAdLightLoading(false);
    }
  }, [initialAdLight, router.query]);

  raptiveAdManager.configure({
    footerDisabled: disableRaptiveFooter,
    targeting: initialTargeting,
  });

  if (!disableRaptiveReadyOnPageLoad) {
    raptiveAdManager.setReady(true);
  }

  useEffect(() => {
    const fromServer = partnerReferralAdLightFromParams(initialQueryParams);
    const isReferredBySofi = isSofiPartnerReferral(router.query as Record<string, string>);
    const isReferredByRobinhood = isRobinhoodPartnerReferral(router.query as Record<string, string>);
    const shouldReferrerHaveAdLight = fromServer || isReferredBySofi || isReferredByRobinhood;
    if (shouldReferrerHaveAdLight) return;

    const handleEnableRaptiveAds = () => {
      !disableRaptiveReadyOnPageLoad && raptiveAdManager.setReady(true);
    };

    const applyAdLightSettings = (hasAccess: boolean) => {
      startTransition(() => {
        setHasAdLight(hasAccess);
        setIsConnatixDisabled(hasAccess);
        hasAccess && raptiveAdManager.enableAdLightMode();
        setIsRaptiveDisabled(false);
        handleEnableRaptiveAds();
        setIsAdLightLoading(false);
      });
    };

    const clearCacheAndDisable = () => {
      persistAndApply(false);
    };

    const getAdLightAccess = () => !!permissionsManager.hasAccess('com/ad-block', 'light')?.ok;

    const persistAndApply = (hasAccess: boolean) => {
      localStorage.setItem('adlight', String(hasAccess));
      applyAdLightSettings(hasAccess);
    };

    const initialize = async () => {
      try {
        const cachedRaw = localStorage.getItem('adlight');
        if (cachedRaw !== null) {
          const cachedHasAccess = cachedRaw === 'true';
          applyAdLightSettings(cachedHasAccess);
          authManager.getAuthSession().then(() => {
            if (!authManager.isLoggedIn()) {
              clearCacheAndDisable();
              return;
            }
            const hasAccess = getAdLightAccess();
            if (hasAccess !== cachedHasAccess) persistAndApply(hasAccess);
          }, clearCacheAndDisable);
        } else {
          await authManager.getAuthSession();
          if (!authManager.isLoggedIn()) {
            clearCacheAndDisable();
            return;
          }
          persistAndApply(getAdLightAccess());
        }
      } catch {
        clearCacheAndDisable();
      }
    };

    initialize();

    const authSubscription = authManager.subscribe(event => {
      if (event.type === 'authentication:logged_out') clearCacheAndDisable();
    });

    return () => {
      raptiveAdManager.cleanup();
      authSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const syncEdgeSubscription = () => {
      const hasAccess = !!permissionsManager.hasAccess(EDGE_PERMISSION_ACTION, EDGE_PERMISSION_RESOURCE)?.ok;
      startTransition(() => setHasEdgeSubscription(hasAccess));
    };

    const resolveInitialSession = async () => {
      try {
        await authManager.getAuthSession();
      } catch {
        // ignore — we still fall through to read current state
      }
      startTransition(() => {
        setIsLoggedIn(authManager.isLoggedIn());
        syncEdgeSubscription();
        setIsEdgeLoading(false);
      });
    };

    resolveInitialSession();

    const permissionSubscription = permissionsManager.subscribe((event: PermissionsManagerEvent) => {
      if (event.type === 'permission_changed') syncEdgeSubscription();
    });

    const authSubscription = authManager.subscribe((event: AuthenticationManagerEvent) => {
      switch (event.type) {
        case 'unauthorized':
          startTransition(() => {
            setIsLoggedIn(false);
            setHasEdgeSubscription(false);
          });
          break;
        case 'authentication:session_update':
          startTransition(() => {
            setIsLoggedIn(authManager.isLoggedIn());
            syncEdgeSubscription();
          });
          break;
        case 'authentication:logged_out':
          startTransition(() => {
            setIsLoggedIn(false);
            setHasEdgeSubscription(false);
          });
          break;
      }
    });

    return () => {
      permissionSubscription.unsubscribe();
      authSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disabled = useMemo<DisabledAds>(
    () => ({
      connatix: isConnatixDisabled,
      raptive: isRaptiveDisabled,
    }),
    [isConnatixDisabled, isRaptiveDisabled],
  );

  const contextValue = useMemo<BenzingaEdgeContextInterface>(
    () => ({
      adLightEnabled: hasAdLight,
      disabled,
      hasEdgeSubscription,
      isEdgeSubscriptionLoading: isEdgeLoading,
      isLoading: isAdLightLoading,
      isLoggedIn,
    }),
    [hasAdLight, disabled, hasEdgeSubscription, isEdgeLoading, isAdLightLoading, isLoggedIn],
  );

  return <BenzingaEdgeContext.Provider value={contextValue}>{children}</BenzingaEdgeContext.Provider>;
};

export const useBenzingaEdge = () => {
  const isBzApp = appEnvironment().isApp(appName.bz);
  const context = useContext(BenzingaEdgeContext);
  if (!isBzApp || !context) {
    return {
      adLightEnabled: false,
      disabled: {
        connatix: false,
        raptive: false,
      },
      hasEdgeSubscription: false,
      isEdgeSubscriptionLoading: false,
      isLoading: false,
      isLoggedIn: false,
    };
  }
  if (context === null) {
    console.error('useBenzingaEdge must be used within a BenzingaEdgeProvider');
  }
  return context;
};
